import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import UserContext from "../../common/UserContext";
import {AssignedProductContext, GeneralContext, SelectedProductContext} from "../FSARequestLicenseContext";
import {useLocalization} from "@progress/kendo-react-intl";
import axios from "axios";
import config from "../../../config";
import {PanelBar, PanelBarItem} from "@progress/kendo-react-layout";
import {RequestDownloadGrid} from "./RequestDownloadGrid";
import {Col, Row} from "reactstrap";
import {
    downloadAllLicensesKey,
    mailAllKey,
    mainMessages,
    selectMoreKey
} from "../../../assets/text/MultilingualText";
import {Button} from "@progress/kendo-react-buttons";
import {RequestPopovers} from "../../common/RequestLicense/RequestPopovers";
import {RequestAlerts} from "../../common/RequestLicense/RequestAlerts";
import {Tooltip} from "@progress/kendo-react-tooltip";
import {DownloadIcon, IconButton, MailIcon} from "../../common/icons";
import {EmailDialog} from "../../common/EmailModal";
import {DownloadPanelBarHeader} from "./DownloadPanelBarHeader";
import {downloadLicenses} from "../../common/utilities";
import uuid from "react-uuid";
import {useSupportContext} from "../../common/SupportContext";

export const PopoverContext = createContext({
    onMouseOver: () => null,
});

export const LicenseDownload = (props) => {
    const {
        siteLanguageDefault,
        accessToken,
        timeout,
    } = useContext(UserContext);

    const {
        setIsLoading,
        uniqueID,
        parentUniqueID,
        stepperItems,
        setStepperItems,
        handleBeforeUnload,
        headerInfo,
        setHeaderInfo,
        changeStepActivation,
    } = useContext(GeneralContext);

    const {
        selectedProducts,
        setSelectedProducts,
    } = useContext(SelectedProductContext);

    const {
        assignedProducts,
        setAssignedProducts,
    } = useContext(AssignedProductContext);

    const { dispatch } = useSupportContext();

    const {
        requestedProducts,
        setRequestedProducts,
    } = props

    const localization = useLocalization();

    const requestSource = 'REQ_LIC_FSA'

    const [showPopover, setShowPopover] = useState(false);
    const [popoverType, setPopoverType] = useState('generalError');
    const [popoverRef, setPopoverRef] = useState();
    const [showEmailModal, setShowEmailModal] = useState(false);

    const [disableEmailDownloadButtons, setDisableEmailDownloadButtons] = useState(true);
    const [disableAssignMoreStepperButtons, setDisableAssignMoreStepperButtons] = useState(true);

    const initIsVisible = {
        requestLicenseInProgress: true,
        requestLicenseSuccess: false,
        cloudAlert: false,
        supportError: false,
        timeoutError: false,
        generationError: false,
        qtyError: false,
        entitlementError: false,
        emailSuccess: false,
    }
    const [isVisibleAlerts, setIsVisibleAlerts] = useState(initIsVisible);

    //Sets which popover to show and it's corresponding reference
    const handlePopoverMouseOver = useCallback(
        (event) => {
            if (event.show) {
                setShowPopover(true);
            } else {
                setShowPopover(false);
            }
            if (event.cloudAlert) {
                setPopoverType('cloudAlert');
            } else if (event.supportError) {
                setPopoverType('supportError');
            } else if (event.timeoutError) {
                setPopoverType('timeoutError');
            } else if (event.generationError) {
                setPopoverType('generationError');
            } else if (event.qtyError) {
                setPopoverType('qtyError');
            } else {
                setPopoverType('entitlementError');
            }

            setPopoverRef(event.popoverRef);
        },
        [setShowPopover, setPopoverType, setPopoverRef]
    );
    //Show success alert
    useEffect(() => {
        const requiredCount = requestedProducts.length;
        let successCount = 0

        requestedProducts.forEach(host => {
            if (host.icons.download || host.icons.cloudAlert) {
                successCount++
            }
        })

        if (successCount === requiredCount) {
            setIsVisibleAlerts(isVisibleAlerts => ({
                ...isVisibleAlerts,
                requestLicenseInProgress: false,
                requestLicenseSuccess: true
            }));
        }

    }, [requestedProducts])

    //Keeps track of what transaction ids are downloadable
    const [downloadableIDs, setDownloadableIDs] = useState([]);
    useEffect(() => {
        let transactionIDs = [];
        requestedProducts.forEach(host => {
            if (host.icons.download && host.icons.checked) {
                transactionIDs.push(host.transaction_id);
            }
        })
        setDownloadableIDs(transactionIDs);

        // Update Support Metadata for assigned products
        if (requestedProducts.length !== 0) {
            dispatch({ type: 'UPDATE_TRANSACTION', payload: {requested: requestedProducts}});
        }
    }, [requestedProducts])

    //Enable or disable email/download selected license button
    useEffect(() => {
        let notFinishedLoading = false;
        let isDownloadable = false;
        requestedProducts.forEach(host => {
            //Enable buttons if all hosts are done loading and at least one downloadable host
            if (host.icons.loading) {
                notFinishedLoading = true;
            }

            if (host.icons.download && host.icons.checked) {
                isDownloadable = true;
            }
        })

        if (notFinishedLoading) {
            setDisableEmailDownloadButtons(true);
        } else {
            if (isDownloadable) {
                setDisableEmailDownloadButtons(false);
            } else {
                setDisableEmailDownloadButtons(true);
            }
        }
    }, [requestedProducts])

    //Enable or disable assign more button and stepper
    useEffect(() => {
        let notFinishedLoading = false;
        requestedProducts.forEach(host => {
            //Enable buttons if all hosts are done loading
            if (host.icons.loading) {
                notFinishedLoading = true;
            }
        })
        setDisableAssignMoreStepperButtons(notFinishedLoading)
        let updateStepper = JSON.parse(JSON.stringify(stepperItems));
        updateStepper[0].disabled = notFinishedLoading

        if (!notFinishedLoading) {
            updateStepper[1].disabled = selectedProducts.length === 0;
        } else {
            updateStepper[1].disabled = true
        }
        setStepperItems(updateStepper)
    }, [requestedProducts, selectedProducts])

    //Request new license for every host panel bar
    useEffect(() => {

        async function requestNewLicense(host, index) {
            let headers = {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken
            };

            let products = []
            for (const product of host.products) {
                products.push({
                    cart_id: product[uniqueID]
                })
            }

            let data = {
                module: "FSA",
                sub_module: "LICENSE",
                action: "REDEEM",
                input_json: {
                    "transaction_id": host.transaction_id,
                    "products": products,
                    "host_id": host.host_id,
                    "serial_id": host.serial_id,
                    "fsa_id": headerInfo.fsa_id
                }
            }
            // console.log('redeem license',host.transaction_id, data)

            let savedHost = requestedProducts[index];
            axios.post(
                config.fsa_request_license.REDEEM,
                data,
                {headers: headers, timeout: timeout}
            )
                .then((response) => {
                    if (response.status === 200) {
                        //update icons
                        if (response.data['license_generated'] === 'YES') {
                            let licenseType = response.data['license_type']
                            switch (licenseType) {
                                case "FILE":
                                case "KEY":
                                    savedHost['icons']['download'] = true;
                                    savedHost['icons']['loading'] = false;
                                    break;
                                case "CLOUD":
                                    savedHost['icons']['cloudAlert'] = true;
                                    savedHost['icons']['loading'] = false;
                                    break;
                                default:
                                    break;
                            }
                        }

                        if (response.data['license_generated'] === 'PENDING') {
                            savedHost['icons']['specialAlert'] = true;
                            savedHost['icons']['loading'] = false;
                        }
                    }
                    setRequestedProducts([...requestedProducts]);

                    //remove selected qty
                    for (const assignProduct of host.products) {
                        const parentIndex = selectedProducts.findIndex(item => item[uniqueID] === assignProduct[parentUniqueID])

                        selectedProducts[parentIndex].quantity -= assignProduct.assign_quantity
                        selectedProducts[parentIndex].saved_selected_quantity -= assignProduct.assign_quantity
                        selectedProducts[parentIndex].saved_assigned_quantity -= assignProduct.assign_quantity

                        if (selectedProducts[parentIndex].quantity === 0) {
                            selectedProducts.splice(parentIndex, 1)
                        }
                    }
                    setSelectedProducts([...selectedProducts])

                    //remove redeemed hosts
                    let index = assignedProducts.findIndex(host => host.transaction_id === savedHost.transaction_id)
                    assignedProducts.splice(index, 1)
                    setAssignedProducts([...assignedProducts])

                    //update header info
                    setHeaderInfo(response.data['header_info'])
                })
                .catch((error) => {
                    console.log("ERROR: Failed to request new license", error);
                    setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, requestLicenseInProgress: false}));
                    savedHost['icons']['loading'] = false;
                    let retry = false
                    if (!error.response) { //Internet disconnected
                        savedHost['icons']['generationError'] = true;
                        setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, generationError: true}));
                    } else if (error.toString().includes('timeout')) { //Axios timeout
                        savedHost['icons']['supportError'] = true;
                        setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, supportError: true}));
                    } else {
                        if (error.response.status === 408 || error.response.status === 504) { //API & gateway timeout
                            savedHost['icons']['supportError'] = true;
                            setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, supportError: true}));
                        }
                        if (error.response.hasOwnProperty('data')) {
                            let errorCode = error.response?.data?.error_code?.toUpperCase();
                            switch (errorCode) {
                                case "MOD_LICENSE_228": //Plugin Fail
                                    savedHost['icons']['supportError'] = true;
                                    setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, supportError: true}));
                                    break;
                                case "MOD_LICENSE_220": //Qty consumed & user can retry
                                    setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, qtyError: true}));
                                    savedHost['icons']['qtyError'] = true;
                                    retry = true
                                    break;
                                default: //Backend/DB error/rollback & user can retry
                                    savedHost['icons']['entitlementError'] = true;
                                    setIsVisibleAlerts(isVisibleAlerts => ({
                                        ...isVisibleAlerts,
                                        entitlementError: true
                                    }));
                                    retry = true
                                    break
                            }
                            if (error?.response?.data.hasOwnProperty('header_info')) {
                                //update header info
                                setHeaderInfo(error.response.data['header_info'])
                            }
                        }
                    }
                    setRequestedProducts([...requestedProducts]);

                    //Hosts that can retry should appear again in license preview
                    let index = assignedProducts.findIndex(host => host.transaction_id === savedHost.transaction_id)
                    if (retry) {
                        assignedProducts[index].transaction_id = uuid()
                    } else {
                        //remove selected qty
                        for (const assignProduct of host.products) {
                            const parentIndex = selectedProducts.findIndex(item => item[uniqueID] === assignProduct[parentUniqueID])

                            selectedProducts[parentIndex].quantity -= assignProduct.assign_quantity
                            selectedProducts[parentIndex].saved_selected_quantity -= assignProduct.assign_quantity -= assignProduct.assign_quantity
                            selectedProducts[parentIndex].saved_assigned_quantity -= assignProduct.assign_quantity

                            if (selectedProducts[parentIndex].quantity === 0) {
                                selectedProducts.splice(parentIndex, 1)
                            }
                        }
                        setSelectedProducts([...selectedProducts])

                        //remove assigned host
                        assignedProducts.splice(index, 1)
                    }
                    setAssignedProducts([...assignedProducts])
                });
        }

        requestedProducts.forEach((host, index) => {
            requestNewLicense(host, index)
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <PopoverContext.Provider
                value={{
                    onMouseOver: handlePopoverMouseOver
                }}
            >
                {requestedProducts.map((host, index) => {
                    return (
                        <PanelBar
                            key={index}
                            isControlled={true}
                            expanded={['.0']}
                            className={'ksm-panelbar-default ksm-panelbar-no-arrow'}
                        >
                            <PanelBarItem
                                title={
                                    <DownloadPanelBarHeader
                                        handleBeforeUnload={handleBeforeUnload}
                                        requestedProducts={requestedProducts}
                                        setRequestedProducts={setRequestedProducts}
                                        index={index}
                                        setIsLoading={setIsLoading}
                                        requestSource={requestSource}
                                    />
                                }
                            >
                                <RequestDownloadGrid
                                    items={host.products}
                                />
                            </PanelBarItem>
                        </PanelBar>
                    )
                })}
            < /PopoverContext.Provider>
            <RequestPopovers
                type={popoverType}
                showPopover={showPopover}
                popoverRef={popoverRef}
            />
            <Row
                style={{
                    marginBottom: '0.938rem'
                }}
            >
                <Col>
                    <Button
                        themeColor={"primary"}
                        size={"large"}
                        fillMode={"outline"}
                        disabled={disableAssignMoreStepperButtons}
                        onClick={() => {
                            dispatch({ type: 'UPDATE_TRANSACTION', payload: {lic_action: ''}});
                            changeStepActivation(0)
                        }}
                    >
                        {localization.toLanguageString(selectMoreKey, mainMessages[siteLanguageDefault][selectMoreKey])}
                    </Button>
                </Col>
                <Col>
                    <RequestAlerts
                        isVisibleAlerts={isVisibleAlerts}
                        setIsVisibleAlerts={setIsVisibleAlerts}
                    />
                </Col>
                <Col>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'end',
                            flexWrap: 'wrap',
                            gap: '0.5rem'
                        }}
                    >
                        <Tooltip
                            anchorElement="target"
                            showCallout={true}
                            parentTitle={true}
                            openDelay={0}
                            position="left"
                        >
                            <IconButton
                                title={localization.toLanguageString(mailAllKey, mainMessages[siteLanguageDefault][mailAllKey])}
                                themeColor={"primary"}
                                fillMode={"solid"}
                                size={"large"}
                                disabled={disableEmailDownloadButtons}
                                onClick={() => {
                                    dispatch({ type: 'UPDATE_TRANSACTION', payload: {lic_action: 'email'}});
                                    setShowEmailModal(true)
                                }}
                                Icon={MailIcon}
                            />
                        </Tooltip>
                        <Tooltip
                            anchorElement="target"
                            showCallout={true}
                            parentTitle={true}
                            openDelay={0}
                            position="left"
                        >
                            <IconButton
                                title={localization.toLanguageString(downloadAllLicensesKey, mainMessages[siteLanguageDefault][downloadAllLicensesKey])}
                                themeColor={"primary"}
                                fillMode={"solid"}
                                size={"large"}
                                onClick={() => {
                                    dispatch({ type: 'UPDATE_TRANSACTION', payload: {lic_action: 'download all'}});
                                    downloadLicenses(downloadableIDs, accessToken, setIsLoading, handleBeforeUnload, requestSource);
                                }}
                                disabled={disableEmailDownloadButtons}
                                Icon={DownloadIcon}
                            />
                        </Tooltip>
                    </div>
                </Col>
            </Row>
            {showEmailModal && (
                <EmailDialog
                    setShowEmailModal={setShowEmailModal}
                    setShowEmailSuccess={() => {
                        setIsVisibleAlerts(isVisibleAlerts => ({...isVisibleAlerts, emailSuccess: true}))
                    }}
                    transactionIDs={downloadableIDs}
                    setIsLoading={setIsLoading}
                    request_source={requestSource}
                />
            )}
        </>
    )
}